import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AdministrativeConsolidatedSchedulesTransferService } from 'src/app/services/administrative-consolidated-schedules-transfer.service';
import { LoginService } from 'src/app/services/login.service';

export interface TransferConfirmationData {
  doctorName: string | null;
  specialtyName: string | null;
  amount: number | null;
  indicationId: number | null;
  bulk?: {
    totalAmount: number;
    totalCount: number;
    consolidatedScheduleIds: string[];
  } | null;
  consolidatedScheduleId: string | null;
}

@Component({
  selector: 'app-transfer-confirmation-modal',
  templateUrl: './transfer-confirmation.modal.html',
  styleUrls: ['./transfer-confirmation.modal.scss'],
})
export class TransferConfirmationModalComponent {
  loading = false;

  constructor(
    private readonly dialogRef: MatDialogRef<TransferConfirmationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TransferConfirmationData,
    private readonly transferService: AdministrativeConsolidatedSchedulesTransferService,
    private readonly loginService: LoginService
  ) {}

  get formattedAmount(): string {
    if (this.data.amount === null || this.data.amount === undefined) return '-';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(Number(this.data.amount));
  }

  get formattedBulkAmount(): string {
    const value = this.data.bulk?.totalAmount;
    if (value === null || value === undefined) return '-';

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(Number(value));
  }

  get isBulk(): boolean {
    return !!this.data.bulk;
  }

  close(): void {
    if (this.loading) return;
    this.dialogRef.close();
  }

  confirm(): void {
    if (this.loading) return;
    this.loading = true;

    const bulkIds = this.data.bulk?.consolidatedScheduleIds ?? [];
    if (this.isBulk && bulkIds.length === 0) {
      this.loading = false;
      this.dialogRef.close();
      return;
    }

    if (!this.isBulk && !this.data.consolidatedScheduleId) {
      this.loading = false;
      this.dialogRef.close();
      return;
    }

    const request$ = this.isBulk
      ? this.transferService.batchTransfer(bulkIds)
      : this.transferService.transferSingle(this.data.consolidatedScheduleId ?? '');

    request$.subscribe({
      next: () => this.dialogRef.close('success'),
      error: (error) => this.handleError(error),
    });
  }

  private handleError(error: any): void {
    this.loading = false;
    this.loginService.showMessage(this.getFriendlyError(error), 5000);
    this.dialogRef.close();
  }

  private getFriendlyError(error: any): string {
    const status = error?.status;
    const backendMessage =
      error?.error?.message ||
      (typeof error?.error === 'string' ? error.error : null);

    if (status === 404) {
      return 'Serviço de pagamentos indisponível no momento. Tente novamente em instantes ou contate o suporte.';
    }

    if (status >= 500) {
      return 'Erro temporário no serviço de pagamentos. Tente novamente em alguns instantes.';
    }

    if (status === 400 || status === 422) {
      return (
        backendMessage ||
        'Não foi possível processar o pagamento. Verifique os dados e tente novamente.'
      );
    }

    return (
      backendMessage ||
      'Falha ao iniciar o pagamento. Tente novamente ou contate o suporte.'
    );
  }
}
